import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent, type AccentKey } from './theme';

export interface ChallengeItem {
  icon: string;
  title: string;
  desc: string;
}

interface SolutionChallengeProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  items: ChallengeItem[];
  variant?: 'grid' | 'timeline';
  accentKey: AccentKey;
}

export default function SolutionChallenge({
  eyebrow,
  title,
  subtitle,
  items,
  variant = 'grid',
  accentKey,
}: SolutionChallengeProps) {
  const a = accent(accentKey);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={eyebrow} title={title} subtitle={subtitle} />

        {variant === 'grid' ? (
          <Reveal className="mt-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-lg bg-background-50 border border-background-200 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-md ${a.iconBox}`}>
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="mt-5 font-heading font-bold text-foreground-950 text-lg leading-snug">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        ) : (
          <>
            {/* 桌面端横向时间线 */}
            <Reveal className="mt-12 hidden lg:block">
              <div className="relative grid grid-cols-4 gap-6">
                <div className="absolute top-8 left-0 right-0 h-px bg-background-300"></div>
                {items.map((item, idx) => (
                  <div key={item.title} className="relative flex flex-col items-center text-center">
                    <div
                      className={`relative z-10 w-16 h-16 flex items-center justify-center rounded-full bg-background-50 border-2 ${a.strongBorder} ${a.text}`}
                    >
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                    <div className="mt-4 text-xs font-semibold text-foreground-400">0{idx + 1}</div>
                    <div className="mt-1 font-heading font-bold text-foreground-950 text-base">{item.title}</div>
                    <div className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* 移动端纵向时间线 */}
            <Reveal className="mt-10 lg:hidden">
              <div className="flex flex-col gap-4">
                {items.map((item, idx) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-12 h-12 flex items-center justify-center rounded-full bg-background-50 border-2 ${a.strongBorder} ${a.text} shrink-0`}
                      >
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      {idx < items.length - 1 && <div className={`w-px flex-1 mt-2 ${a.line}`}></div>}
                    </div>
                    <div className="pb-6">
                      <div className="text-xs font-semibold text-foreground-400">0{idx + 1}</div>
                      <div className="mt-1 font-heading font-bold text-foreground-950">{item.title}</div>
                      <div className="mt-1 text-sm text-foreground-600 leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </>
        )}
      </div>
    </section>
  );
}